// Pure view-model of the Map screen (WEB_PLAN.md §5.7; ports of MapCanvasView.filtered / pinSet / legend and
// MapTab+Store.repsWithLocation): visible stores → filtered stores → pins on the map, the legend with counts, territory
// overlays and rep markers. No React and no MapLibre, so it runs in unit tests; MapPage memoizes each step separately
// so changing "Color by" recolors pins without refiltering 5,000 stores.

import { MAP_COLORIZES, type MapColorize } from '@/contracts/enums';
import type { RecordPayload, TerritoryPayload, UserPayload } from '@/contracts/models';
import { formatElapsed } from '@/domain/format';
import { isValidCoordinate } from '@/domain/geo';
import {
  allTags,
  applyFilter,
  filterFromQuery,
  filterToQuery,
  isFilterActive,
  legend,
  pinColorHex,
  pinLabel,
  type FilterContext,
  type LegendEntry,
  type MapFilter,
  type RecordLookups,
} from '@/domain/records';
import { PRESENCE_TITLES, fullName, initials, presenceOf, type Presence, type RepPosition } from '@/domain/team';
import { compareTerritories } from '@/domain/territories';
import { compareLocalized, idKey } from '@/domain/util';
import type { MapMarker, MapPoint, MapPolygon } from '@/features/common';

// MARK: - Stores and pins

export interface MapStores<R extends RecordPayload = RecordPayload> {
  /** Stores matching the filter, with or without a location. */
  matching: R[];
  /** The ones the map can draw: matching stores with a valid, non-(0,0) coordinate (`Geo.swift:20`). */
  located: R[];
  /** Matching stores that have no usable location, so are not on the map. */
  unlocatedCount: number;
  /** Visible stores before the filter ("N of M stores match your filters"). */
  totalVisible: number;
  filterActive: boolean;
}

/** `MapCanvasView.filtered`: the visible stores through the shared filter, then only those with a coordinate. */
export function filterMapStores<R extends RecordPayload>(visible: readonly R[], filter: MapFilter, ctx: FilterContext): MapStores<R> {
  const matching = applyFilter(visible, filter, ctx);
  const located = matching.filter((r) => isValidCoordinate(r.coordinate));
  return {
    matching,
    located,
    unlocatedCount: matching.length - located.length,
    totalVisible: visible.length,
    filterActive: isFilterActive(filter),
  };
}

/** One pin per located store, colored and labelled exactly as `pinColorHex` / `pinLabel` (AppStore.swift:204). */
export function storePins(located: readonly RecordPayload[], mode: MapColorize, lk: RecordLookups): MapPoint[] {
  return located.map((r) => ({ id: r.id, coordinate: r.coordinate, color: pinColorHex(r, mode, lk), label: pinLabel(r, mode, lk) }));
}

/** `legend(for:records:)` over the given pins (MapPage passes the ones in view): one row per label with its count, in the iOS order. */
export function mapLegend(located: readonly RecordPayload[], mode: MapColorize, lk: RecordLookups): LegendEntry[] {
  return legend(mode, located, lk);
}

/** Sum of the legend counts (equals the number of pins). */
export function legendTotal(entries: readonly LegendEntry[]): number {
  return entries.reduce((n, e) => n + e.count, 0);
}

export interface MapView extends MapStores {
  points: MapPoint[];
  legend: LegendEntry[];
}

/** The whole pipeline in one call (tests and small screens); MapPage runs the steps with separate memos. */
export function buildMapView(input: {
  visible: readonly RecordPayload[];
  filter: MapFilter;
  ctx: FilterContext;
  mode: MapColorize;
  lookups: RecordLookups;
}): MapView {
  const stores = filterMapStores(input.visible, input.filter, input.ctx);
  return {
    ...stores,
    points: storePins(stores.located, input.mode, input.lookups),
    legend: mapLegend(stores.located, input.mode, input.lookups),
  };
}

// MARK: - Tags

export interface TagOption {
  tag: string;
  /** Visible stores carrying the tag exactly as spelled (what choosing the chip matches). */
  count: number;
}

/**
 * The filter panel's Tags section: every tag on the visible stores (`allTags`: de-duplicated case-insensitively,
 * alphabetical), each with how many stores carry it. The filter matches tags exactly (iOS `filter.tags.contains`), so
 * the count is of that exact spelling, and a store with a tag twice counts once. Chosen tags no visible store has
 * (a shared link, or the last store with it was reassigned away) stay listed with 0, so they can be turned off.
 */
export function tagOptions(records: readonly RecordPayload[], chosen: readonly string[] = []): TagOption[] {
  const counts = new Map<string, number>();
  for (const r of records) {
    const tags = r.tags.length > 1 ? new Set(r.tags) : r.tags;
    for (const t of tags) if (t !== '') counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  const tags = allTags(records);
  const listed = new Set(tags);
  const extra = chosen.filter((t) => t !== '' && !listed.has(t));
  const all = extra.length === 0 ? tags : [...tags, ...new Set(extra)].sort(compareLocalized);
  return all.map((tag) => ({ tag, count: counts.get(tag) ?? 0 }));
}

// MARK: - Territories layer

/** Territories drawn on the map: active ones with at least 3 points (MapCanvasView.territories), oldest first. */
export function territoryPolygons(territories: readonly TerritoryPayload[]): MapPolygon[] {
  return territories
    .filter((t) => t.isActive && t.polygon.length >= 3)
    .sort(compareTerritories)
    .map((t) => ({ id: t.id, name: t.name.trim() === '' ? 'Untitled territory' : t.name, color: t.colorHex, points: t.polygon }));
}

// MARK: - Reps layer

/**
 * Rep markers (`repsWithLocation`, managers only): visible, active people whose last breadcrumb has a valid
 * coordinate, colored by live presence. iOS leaves out the phone's own user because it draws the device location;
 * the console has no GPS, so the viewer's own last position is drawn like anyone else's. Freshest last, so the most
 * recent positions are drawn on top.
 */
export function repMarkers(
  positions: ReadonlyMap<string, RepPosition>,
  visibleUsers: readonly UserPayload[],
  now: Date,
  colors: Readonly<Record<Presence, string>>,
): MapMarker[] {
  const out: { marker: MapMarker; at: number }[] = [];
  for (const u of visibleUsers) {
    if (!u.isActive) continue;
    const p = positions.get(idKey(u.id));
    if (!p || !isValidCoordinate(p.coordinate)) continue;
    const presence = presenceOf(p.at, now);
    const name = fullName(u) || 'Unnamed';
    out.push({
      at: p.at.getTime(),
      marker: {
        id: u.id,
        coordinate: p.coordinate,
        color: colors[presence],
        label: initials(u) || '?',
        title: `${name} · ${PRESENCE_TITLES[presence]} · ${formatElapsed(p.at, now)}`,
      },
    });
  }
  return out.sort((a, b) => a.at - b.at).map((x) => x.marker);
}

// MARK: - URL state

/** The query key for a "Color by" override (absent = the company default, `settings.defaultMapColorize`). */
export const COLOR_PARAM = 'color';

export function parseColorize(value: string | null | undefined): MapColorize | null {
  return value && (MAP_COLORIZES as readonly string[]).includes(value) ? (value as MapColorize) : null;
}

export interface MapUrlState {
  filter: MapFilter;
  /** The session's "Color by" override, or null for the company default. */
  colorize: MapColorize | null;
}

/** Reads the filter (domain `filterFromQuery`) and the color override from the page's query. */
export function readMapQuery(params: URLSearchParams): MapUrlState {
  return { filter: filterFromQuery(params), colorize: parseColorize(params.get(COLOR_PARAM)) };
}

/** The query for a map view: filter ids (never search text, WEB_PLAN.md §5.5), then the color override if any. */
export function mapQuery(state: MapUrlState, defaultColorize: MapColorize): URLSearchParams {
  const p = filterToQuery(state.filter);
  if (state.colorize && state.colorize !== defaultColorize) p.set(COLOR_PARAM, state.colorize);
  return p;
}

/** True when the query carries any map state (so a remembered view is not restored over a shared link). */
export function hasMapState(params: URLSearchParams): boolean {
  const s = readMapQuery(params);
  return s.colorize !== null || filterToQuery(s.filter).toString() !== '';
}

/**
 * The query to put back when /map opens: the remembered view, normalized (keys and values the map no longer reads
 * dropped, a color equal to the company default left out), or null when the page's own query already carries map
 * state (a shared link wins) or nothing usable is left. Restoring only what parses back to map state is what stops
 * the restore redirect from looping on a remembered value that no longer reads as map state.
 */
export function restorableQuery(saved: string | null, current: URLSearchParams, defaultColorize: MapColorize): string | null {
  if (saved === null || saved === '' || hasMapState(current)) return null;
  const q = mapQuery(readMapQuery(new URLSearchParams(saved)), defaultColorize).toString();
  return q === '' ? null : q;
}

// MARK: - Peek and viewport

/** The peeked store while its pin is drawn, else null: a filter that hides the store also hides its peek. */
export function peekOnMap(peekId: string | null, locatedIds: ReadonlySet<string>): string | null {
  return peekId !== null && locatedIds.has(idKey(peekId)) ? peekId : null;
}

/** The map's visible region as MapLibre reports it: west, south, east, north (west/east may pass ±180 on world copies). */
export type ViewBounds = readonly [west: number, south: number, east: number, north: number];

/** Whether a coordinate is inside the visible region, counting the world copies either side of the antimeridian. */
export function inViewBounds(c: { latitude: number; longitude: number }, b: ViewBounds): boolean {
  const [west, south, east, north] = b;
  if (!(c.latitude >= south && c.latitude <= north)) return false;
  if (east - west >= 360) return true;
  // Shift the longitude into [west, west + 360) and compare once.
  const lon = ((((c.longitude - west) % 360) + 360) % 360) + west;
  return lon <= east;
}

/**
 * The located stores whose pins are in the visible region (MapCanvasView.pinSet `visible`, what the iOS legend
 * counts). Without a region yet (map not loaded) every located store counts.
 */
export function storesInView<R extends RecordPayload>(located: readonly R[], bounds: ViewBounds | null): readonly R[] {
  if (!bounds) return located;
  return located.filter((r) => isValidCoordinate(r.coordinate) && inViewBounds(r.coordinate, bounds));
}

/** Same region, so the legend does not recount after a move that ended where it started. */
export function sameBounds(a: ViewBounds | null, b: ViewBounds | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
}

/** What the legend says when it has no rows (iOS "No pins in view" when stores are drawn elsewhere). */
export function legendEmptyText(locatedCount: number, filterActive: boolean): string {
  if (locatedCount > 0) return 'No pins in view';
  return filterActive ? 'No stores match the filters' : 'No stores with a location';
}

/**
 * A tiny external store for the visible region. MapLibre reports it on every `moveend`; only the legend subscribes,
 * so panning re-renders the legend, not the filter panel and the store list.
 */
export interface ViewportStore {
  get(): ViewBounds | null;
  set(bounds: ViewBounds | null): void;
  subscribe(listener: () => void): () => void;
}

export function createViewportStore(): ViewportStore {
  let current: ViewBounds | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => current,
    set(bounds) {
      if (sameBounds(current, bounds)) return;
      current = bounds;
      for (const l of [...listeners]) l();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
